import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUserData } from '../services/userDataService';
import { useAuthService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import profileDataService from '../services/profileDataService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import screamifyLogo from '../img/screamifyweblogo.png';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Divider,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  InputAdornment,
  DialogActions,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import TikTokIcon from '@mui/icons-material/MusicVideo';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ForumIcon from '@mui/icons-material/Forum';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import forumService from '../services/forumService';
import { fetchAuthSession } from 'aws-amplify/auth';
import ReviewCards from '../components/ReviewCards';
import ReviewDetailDialog from '../components/ReviewDetailDialog';

const UserProfile = () => {
  // State and hooks
  const { userData, isLoading, error, updateUserData } = useUserData();
  const { user } = useAuthService();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Threads & comments states
  const [userThreads, setUserThreads] = useState([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [threadsError, setThreadsError] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  // Create thread dialog states
  const [createThreadDialogOpen, setCreateThreadDialogOpen] = useState(false);
  const [threadTitle, setThreadTitle] = useState('');
  const [threadContent, setThreadContent] = useState('');
  const [threadPoster, setThreadPoster] = useState('');

  // Movie search states
  const [movieSearchDialogOpen, setMovieSearchDialogOpen] = useState(false);
  const [movieSearchQuery, setMovieSearchQuery] = useState('');
  const [movieSearchResults, setMovieSearchResults] = useState([]);
  const [movieSearchLoading, setMovieSearchLoading] = useState(false);

  // TMDB API constants (used in movie poster search)
  const TMDB_API_KEY = '2089db4b95439747f34c0374c8c8e254';
  const TMDB_API_TOKEN =
    'eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiIyMDg5ZGI0Yjk1NDM5NzQ3ZjM0YzAzNzRjOGM4ZTI1NCIsIm5iZiI6MTc0MjQ4NTM4NC44MDYwMDAyLCJzdWIiOiI2N2RjMzc4ODhhZjQ1MmYzMGZlOWY4ODkiLCJzY29wZXMiOlsiYXBpX3JlYWQiXSwidmVyc2lvbiI6MX0.QW2np_x0f5S2mEHi_NMzB-eW8bbkmXiOfY9pH1F9hmI';
  const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p/w500';

  // Replace single isEditing state with section-specific editing states
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [isEditingSocial, setIsEditingSocial] = useState(false);
  const [editedBio, setEditedBio] = useState('');
  const [editedTikTok, setEditedTikTok] = useState('');
  const [editedInstagram, setEditedInstagram] = useState('');

  // Replace single updateError state with section-specific error states
  const [bioUpdateError, setBioUpdateError] = useState(null);
  const [socialUpdateError, setSocialUpdateError] = useState(null);

  // Add this after other useState declarations
  const [userVotes, setUserVotes] = useState({});

  // Add this state for copy feedback
  const [copySuccess, setCopySuccess] = useState(false);

  // Add these new state variables for the link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [publicLink, setPublicLink] = useState('');

  // Add new state for profile images
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [bannerImageUrl, setBannerImageUrl] = useState(null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [profileImageError, setProfileImageError] = useState(null);
  const [bannerImageError, setBannerImageError] = useState(null);
  
  // Add refs for file inputs
  const profileImageInputRef = useRef();
  const bannerImageInputRef = useRef();

  // Initialize edit values when userData changes
  useEffect(() => {
    if (userData) {
      setEditedBio(userData.userBio || '');
      setEditedTikTok(userData.userTikTok || '');
      setEditedInstagram(userData.userInstagram || '');
    }
  }, [userData]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Fetch user threads
  useEffect(() => {
    const fetchUserThreads = async () => {
      if (user && user.attributes?.sub) {
        try {
          setIsLoadingThreads(true);
          const token = await fetchAuthSession().then(session => session.tokens?.accessToken?.toString());
          const threads = await forumService.getUserThreads(user.attributes.sub, token);
          setUserThreads(threads);
        } catch (err) {
          console.error('Error fetching threads:', err);
          setThreadsError('Failed to load reviews');
        } finally {
          setIsLoadingThreads(false);
        }
      }
    };
    fetchUserThreads();
  }, [user]);

  // Thread detail dialog & comments handling
  const openThreadDetailDialog = async (thread) => {
    setSelectedThread(thread);
    setIsLoadingComments(true);
    
    try {
      const token = await fetchAuthSession().then(session => session.tokens?.accessToken?.toString());
      const threadDetails = await forumService.getThreadDetails(thread.threadId, token);
      
      // Set comments from the thread details response
      setComments(threadDetails.posts || []);
      setDialogOpen(true);
    } catch (err) {
      console.error('Error fetching thread details:', err);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedThread(null);
    setComments([]);
    setNewComment('');
  };

  const fetchThreadComments = async (threadId) => {
    try {
      setIsLoadingComments(true);
      const token = await fetchAuthSession().then(session => session.tokens?.accessToken?.toString());
      const threadDetails = await forumService.getThreadDetails(threadId, token);
      if (threadDetails && Array.isArray(threadDetails.posts)) {
        const allComments = threadDetails.posts.flatMap(post => post.comments || []);
        setComments(allComments);
      } else {
        setComments([]);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Function to check if user has voted on an item
  const getUserVoteStatus = useCallback((itemType, itemId) => {
    // Using consistent key format
    const key = `${itemType}-${itemId}`;
    return userVotes[key] || null;
  }, [userVotes]);
  
  // Function to generate consistent vote keys
  const getVoteKey = useCallback((itemType, itemId) => {
    return `${itemType}-${itemId}`;
  }, []);

  const handleVote = async (itemId, itemType, voteType, event) => {
    event.stopPropagation();
    try {
      const token = await fetchAuthSession().then(session => session.tokens?.accessToken?.toString());
      await forumService.vote(itemId, itemType, voteType, token);
      // Update vote counts locally for threads and selected thread
      setUserThreads(prevThreads =>
        prevThreads.map(thread =>
          thread.threadId === itemId
            ? {
                ...thread,
                upvotes: voteType === 'upvote' ? thread.upvotes + 1 : thread.upvotes,
                downvotes: voteType === 'downvote' ? thread.downvotes + 1 : thread.downvotes
              }
            : thread
        )
      );
      if (selectedThread && selectedThread.threadId === itemId) {
        setSelectedThread(prev => ({
          ...prev,
          upvotes: voteType === 'upvote' ? prev.upvotes + 1 : prev.upvotes,
          downvotes: voteType === 'downvote' ? prev.downvotes + 1 : prev.downvotes
        }));
      }
      // Update user votes state 
      if (voteType) {
        setUserVotes(prev => ({
          ...prev,
          [getVoteKey(itemType, itemId)]: voteType
        }));
      } else {
        setUserVotes(prev => {
          const newVotes = {...prev};
          delete newVotes[getVoteKey(itemType, itemId)];
          return newVotes;
        });
      }
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedThread) return;
    try {
      const token = await fetchAuthSession().then(session => session.tokens?.accessToken?.toString());
      const response = await forumService.addMessageToThread(
        selectedThread.threadId,
        newComment,
        token,
        userData.userHandle
      );
      
      const commentFromPost = {
        commentId: response.postId || `temp-${Date.now()}`,
        content: response.content,
        createdAt: response.createdAt || new Date().toISOString(),
        userHandle: userData.userHandle,
        upvotes: 0,
        downvotes: 0
      };
      
      setComments(prev => [...prev, commentFromPost]);
      setNewComment('');
      
      await fetchThreadComments(selectedThread.threadId);
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  // Movie search functions
  const handleMovieSearchChange = (e) => setMovieSearchQuery(e.target.value);
  const handleMovieSearchSubmit = async (e) => {
    e.preventDefault();
    if (!movieSearchQuery.trim()) return;
    setMovieSearchLoading(true);
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/search/movie?query=${encodeURIComponent(movieSearchQuery)}&include_adult=false`,
        {
          headers: {
            Authorization: `Bearer ${TMDB_API_TOKEN}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      setMovieSearchResults(data.results || []);
    } catch (error) {
      console.error('Error searching movies:', error);
    } finally {
      setMovieSearchLoading(false);
    }
  };

  const handleCreateThread = async () => {
    if (!threadTitle.trim() || !threadContent.trim()) return;
    try {
      const token = await fetchAuthSession().then(session => session.tokens?.accessToken?.toString());
      const newThread = await forumService.createThread(
        threadTitle,
        threadContent,
        token,
        threadPoster,
        userData.userHandle
      );
      
      // Make sure we're adding the new thread with its poster to the existing array
      // This ensures the threadPoster is preserved
      setUserThreads(prev => [newThread, ...prev]);
      
      setThreadTitle('');
      setThreadContent('');
      setThreadPoster('');
      setCreateThreadDialogOpen(false);
    } catch (err) {
      console.error('Error creating thread:', err);
    }
  };

  const handleSaveBio = async () => {
    try {
      setBioUpdateError(null);
      await updateUserData({
        userBio: editedBio
      });
      setIsEditingBio(false);
    } catch (err) {
      setBioUpdateError('Failed to update bio. Please try again.');
      console.error('Error updating bio:', err);
    }
  };

  const handleSaveSocial = async () => {
    try {
      setSocialUpdateError(null);
      
      // Normalize social media handles by removing @ prefix if it exists
      const normalizedTikTok = editedTikTok.trim().replace(/^@+/, '');
      const normalizedInstagram = editedInstagram.trim().replace(/^@+/, '');
      
      await updateUserData({
        userTikTok: normalizedTikTok,
        userInstagram: normalizedInstagram
      });
      setIsEditingSocial(false);
    } catch (err) {
      setSocialUpdateError('Failed to update social media. Please try again.');
      console.error('Error updating social media:', err);
    }
  };

  // Add a function to get popular horror movies
  const getPopularHorrorMovies = async () => {
    try {
      setMovieSearchLoading(true);
      
      // Get popular horror, thriller and mystery movies
      const response = await fetch(
        `https://api.themoviedb.org/3/discover/movie?api_key=${TMDB_API_KEY}&with_genres=27,53,9648&sort_by=popularity.desc&include_adult=false&page=1`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${TMDB_API_TOKEN}`,
            'accept': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch popular horror movies');
      }
      
      const data = await response.json();
      setMovieSearchResults(data.results || []);
    } catch (error) {
      console.error('Error fetching popular horror movies:', error);
    } finally {
      setMovieSearchLoading(false);
    }
  };

  // Modify setMovieSearchDialogOpen to load popular movies
  const openMovieSearch = () => {
    setMovieSearchQuery('');
    setMovieSearchResults([]);
    setMovieSearchDialogOpen(true);
    getPopularHorrorMovies(); // Load popular horror movies when dialog opens
  };

  // Function to open the link dialog
  const openLinkDialog = () => {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const horrorTreeUrl = `${protocol}//${host}/horrortree/@${userData.userHandle}`;
    setPublicLink(horrorTreeUrl);
    setLinkDialogOpen(true);
  };

  // Function to copy HorrorTree link to clipboard
  const copyHorrorTreeLink = () => {
    navigator.clipboard.writeText(publicLink)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };

  // Generate the profile image urls when user data is available
  useEffect(() => {
    if (userData?.userHandle) {
      const userHandle = userData.userHandle;
      setProfileImageUrl(profileDataService.getProfileAssetUrl('avatar', userHandle));
      setBannerImageUrl(profileDataService.getProfileAssetUrl('banner', userHandle));
    }
  }, [userData]);

  // New function to handle profile image upload
  const handleProfileImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Reset errors
    setProfileImageError(null);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setProfileImageError('Please select an image file');
      return;
    }
    
    try {
      setUploadingProfileImage(true);
      const userHandle = userData.userHandle;
      await profileDataService.uploadProfilePicture(file, userHandle);
      
      // Update the image URL with a cache-busting timestamp
      setProfileImageUrl(profileDataService.getProfileAssetUrl('avatar', userHandle));
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setProfileImageError('Failed to upload profile image. Please try again.');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  // New function to handle banner image upload
  const handleBannerImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Reset errors
    setBannerImageError(null);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setBannerImageError('Please select an image file');
      return;
    }
    
    try {
      setUploadingBanner(true);
      const userHandle = userData.userHandle;
      await profileDataService.uploadProfileBanner(file, userHandle);
      
      // Update the image URL with a cache-busting timestamp
      setBannerImageUrl(profileDataService.getProfileAssetUrl('banner', userHandle));
    } catch (error) {
      console.error('Error uploading banner image:', error);
      setBannerImageError('Failed to upload banner image. Please try again.');
    } finally {
      setUploadingBanner(false);
    }
  };

  // Render loading and error states first
  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#000', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', pt: { xs: 8, sm: 10 } }}>
          <Typography variant="h4" sx={{ color: '#ff0000' }}>Loading Profile...</Typography>
        </Box>
        <Footer />
      </Box>
    );
  }
  
  if (error || !userData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#000', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', pt: { xs: 8, sm: 10 } }}>
          <Typography variant="h4" sx={{ color: '#ff0000' }}>
            {error ? 'Error Loading Profile' : 'No Profile Data Found'}
          </Typography>
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0d0d0d',
        background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0000 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Header />
      <Box sx={{ flex: 1, pt: { xs: 8, sm: 10 }, pb: { xs: 8, sm: 4 }, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ maxWidth: '1200px', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Profile Header with Banner */}
          <Paper
            elevation={3}
            sx={{
              bgcolor: 'rgba(20, 20, 20, 0.85)',
              borderRadius: 3,
              overflow: 'hidden',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 0, 0, 0.2)',
              position: 'relative'
            }}
          >
            {/* Banner Image */}
            <Box sx={{
              height: '200px',
              width: '100%',
              position: 'relative',
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              backgroundImage: bannerImageUrl ? `url(${bannerImageUrl})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottom: '1px solid rgba(255, 0, 0, 0.2)'
            }}>
              {/* Banner Upload Button - More visible */}
              <Box sx={{ 
                position: 'absolute',
                bottom: '15px',
                right: '15px',
                zIndex: 100
              }}>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={bannerImageInputRef}
                  onChange={handleBannerImageUpload}
                />
                <Tooltip title="Upload Banner Image">
                  <IconButton
                    onClick={() => bannerImageInputRef.current.click()}
                    disabled={uploadingBanner}
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      width: 28,
                      height: 28,
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.8)',
                      border: '2px solid #fff',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 0, 0, 0.7)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {uploadingBanner ? (
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                    ) : (
                      <FileUploadIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Profile Content */}
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'center', sm: 'left' }, position: 'relative' }}>
                  {/* Profile Image Container */}
                  <Box sx={{ position: 'relative', width: 160, height: 160, margin: '0 auto' }}>
                    {/* Profile Image */}
                    <Box sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      marginTop: '-80px',  // Pull the avatar up into the banner area
                      border: '4px solid #ff0000',
                      boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
                      backgroundColor: '#000',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Box
                        component="img"
                        src={profileImageUrl || screamifyLogo}
                        alt="Profile"
                        onError={(e) => {
                          e.target.onerror = null; // Prevent infinite error loop
                          e.target.src = screamifyLogo;
                          e.target.style.width = '70%';
                          e.target.style.height = 'auto';
                          e.target.style.objectFit = 'contain';
                        }}
                        sx={{
                          width: profileImageUrl ? '100%' : '70%',
                          height: profileImageUrl ? '100%' : 'auto',
                          objectFit: profileImageUrl ? 'cover' : 'contain'
                        }}
                      />
                    </Box>
                    
                    {/* Upload button - positioned BELOW the circle and adjusted 50px to the right */}
                    <Box sx={{ 
                      position: 'absolute',
                      top: 'calc(100% - 30px)',  // Position below the image but 30px higher (15px + 15px)
                      left: 'calc(50% + 50px)',  // Center horizontally + 50px to the right
                      transform: 'translateX(-50%)', // Center adjustment
                      marginTop: '10px', // Space below the circle
                      zIndex: 100,
                      display: 'flex',
                      justifyContent: 'center'
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        ref={profileImageInputRef}
                        onChange={handleProfileImageUpload}
                      />
                      <Tooltip title="Upload Profile Picture">
                        <IconButton
                          onClick={() => profileImageInputRef.current.click()}
                          disabled={uploadingProfileImage}
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: '#fff',
                            width: 28,
                            height: 28,
                            boxShadow: '0 0 10px rgba(0, 0, 0, 0.8)',
                            border: '2px solid #fff',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 0, 0, 0.7)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          {uploadingProfileImage ? (
                            <CircularProgress size={16} sx={{ color: '#fff' }} />
                          ) : (
                            <FileUploadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, textAlign: { xs: 'center', sm: 'left' }, color: '#fff' }}>
                    {userData.userHandle}
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, textAlign: { xs: 'center', sm: 'left' } }}>
                    {userData.userEmail}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', sm: 'flex-start' }, alignItems: 'center' }}>
                    <Typography sx={{ color: '#fff', fontSize: '1rem' }}>
                      Member since {new Date(userData.createdAt).toLocaleDateString()}
                    </Typography>
                    
                    {/* Horror Tree Public Link Button */}
                    <Button
                      variant="outlined"
                      startIcon={<ShareIcon />}
                      onClick={openLinkDialog}
                      sx={{
                        ml: { xs: 0, sm: 2 },
                        color: '#ff0000',
                        borderColor: 'rgba(255, 0, 0, 0.5)',
                        '&:hover': {
                          borderColor: '#ff0000',
                          backgroundColor: 'rgba(255, 0, 0, 0.1)'
                        },
                        textTransform: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Public Horror Tree Link
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Show upload errors if any */}
          {profileImageError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {profileImageError}
            </Alert>
          )}
          {bannerImageError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {bannerImageError}
            </Alert>
          )}

          {/* About & Social Sections */}
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  bgcolor: 'rgba(20, 20, 20, 0.85)',
                  borderRadius: 3,
                  p: { xs: 3, sm: 4 },
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 0, 0, 0.2)',
                  minHeight: '200px'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff' }}>
                    About Me
                  </Typography>
                  {!isEditingBio ? (
                    <IconButton
                      onClick={() => setIsEditingBio(true)}
                      sx={{
                        color: '#ff0000',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.1)'
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={() => {
                          setIsEditingBio(false);
                          setEditedBio(userData.userBio || '');
                        }}
                        variant="outlined"
                        size="small"
                        sx={{
                          color: '#fff',
                          borderColor: '#fff',
                          '&:hover': {
                            borderColor: '#fff',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveBio}
                        variant="contained"
                        size="small"
                        sx={{
                          bgcolor: '#ff0000',
                          '&:hover': {
                            bgcolor: 'rgba(255, 0, 0, 0.8)'
                          }
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  )}
                </Box>
                {isEditingBio ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedBio}
                    onChange={(e) => setEditedBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.5)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 0, 0, 0.8)' },
                        '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                      }
                    }}
                  />
                ) : (
                  <Typography 
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {userData?.userBio || 'No bio added yet.'}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  bgcolor: 'rgba(20, 20, 20, 0.85)',
                  borderRadius: 3,
                  p: { xs: 3, sm: 4 },
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 0, 0, 0.2)',
                  minHeight: '200px'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff' }}>
                    Social Media
                  </Typography>
                  {!isEditingSocial ? (
                    <IconButton
                      onClick={() => setIsEditingSocial(true)}
                      sx={{
                        color: '#ff0000',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.1)'
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={() => {
                          setIsEditingSocial(false);
                          setEditedInstagram(userData.userInstagram || '');
                          setEditedTikTok(userData.userTikTok || '');
                        }}
                        variant="outlined"
                        size="small"
                        sx={{
                          color: '#fff',
                          borderColor: '#fff',
                          '&:hover': {
                            borderColor: '#fff',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveSocial}
                        variant="contained"
                        size="small"
                        sx={{
                          bgcolor: '#ff0000',
                          '&:hover': {
                            bgcolor: 'rgba(255, 0, 0, 0.8)'
                          }
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {isEditingSocial ? (
                    <>
                      <TextField
                        fullWidth
                        value={editedInstagram}
                        onChange={(e) => setEditedInstagram(e.target.value)}
                        placeholder="Instagram username"
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ color: '#E4405F', mr: 1, fontWeight: 'bold' }}>
                              Instagram:
                            </Typography>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.5)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 0, 0, 0.8)' },
                            '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        value={editedTikTok}
                        onChange={(e) => setEditedTikTok(e.target.value)}
                        placeholder="TikTok username"
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ color: '#00f2ea', mr: 1, fontWeight: 'bold' }}>
                              TikTok:
                            </Typography>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.5)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 0, 0, 0.8)' },
                            '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                          }
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography sx={{ color: '#E4405F', fontWeight: 'bold', width: '100px' }}>
                          Instagram:
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          {userData?.userInstagram ? `@${userData.userInstagram.replace(/^@+/, '')}` : 'No Instagram username added'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ color: '#00f2ea', fontWeight: 'bold', width: '100px' }}>
                          TikTok:
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          {userData?.userTikTok ? `@${userData.userTikTok.replace(/^@+/, '')}` : 'No TikTok username added'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Reviews Section */}
          <ReviewCards
            threads={userThreads}
            loading={isLoadingThreads}
            error={threadsError}
            onOpenThread={openThreadDetailDialog}
            onVote={handleVote}
            onCreateReview={openMovieSearch}
            getUserVoteStatus={getUserVoteStatus}
            canCreate={true}
            title="My Reviews"
            emptyMessage="No reviews yet"
            emptyDescription="Share your thoughts about horror, thriller, and mystery movies!"
            showAds={false}
            itemsPerPage={4}
          />

          {/* Use ReviewDetailDialog instead of the original dialog */}
          <ReviewDetailDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            selectedThread={selectedThread}
            comments={comments}
            isLoadingComments={isLoadingComments}
            newComment={newComment}
            setNewComment={setNewComment}
            onAddComment={addComment}
            onVote={handleVote}
            getUserVoteStatus={getUserVoteStatus}
          />

          {/* Create Thread Dialog */}
          <Dialog
            open={createThreadDialogOpen}
            onClose={() => setCreateThreadDialogOpen(false)}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(20, 20, 20, 0.95)',
                border: isMobile ? 'none' : '1px solid rgba(255, 0, 0, 0.3)',
                boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
            TransitionProps={{
              onEnter: () => { if (isMobile) document.body.style.overflow = 'hidden'; },
              onExited: () => { if (isMobile) document.body.style.overflow = 'auto'; }
            }}
          >
            <DialogTitle
              sx={{
                color: '#fff',
                borderBottom: '1px solid rgba(255, 0, 0, 0.3)',
                pb: 1,
                position: isMobile ? 'sticky' : 'relative',
                top: isMobile ? 0 : 'auto',
                zIndex: isMobile ? 1100 : 'auto',
                backgroundColor: 'rgba(20, 20, 20, 0.95)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ForumIcon sx={{ mr: 1, color: '#ff0000' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                  Create Movie Review
                </Typography>
              </Box>
              <IconButton onClick={() => setCreateThreadDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                pt: 3,
                overflow: 'auto',
                flexGrow: 1,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255, 0, 0, 0.6)',
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.2)' }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    width: { xs: '100%', sm: '200px' },
                    flexShrink: 0
                  }}
                >
                  {threadPoster ? (
                    <Box
                      component="img"
                      src={threadPoster}
                      alt="Selected movie poster"
                      sx={{
                        width: '100%',
                        height: '300px',
                        objectFit: 'cover',
                        borderRadius: 1,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
                        transition: 'all 0.5s ease',
                        '&:hover': { transform: 'scale(1.05)', filter: 'brightness(1.1)' }
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 0, 0, 0.1)',
                        borderRadius: 1,
                        border: '2px dashed rgba(255, 0, 0, 0.3)',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setCreateThreadDialogOpen(false);
                        openMovieSearch();
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 48, color: 'rgba(255, 0, 0, 0.5)' }} />
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCreateThreadDialogOpen(false);
                      openMovieSearch();
                    }}
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255, 0, 0, 0.7)',
                      '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.9)' },
                      width: { xs: '180px', sm: 'auto' }
                    }}
                    startIcon={<ImageIcon />}
                  >
                    {threadPoster ? 'Change Poster' : 'Select Movie Poster'}
                  </Button>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.9)',
                        ml: 1,
                        mb: 0.5,
                        display: 'block'
                      }}
                    >
                      Posting as
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: 'rgba(60, 60, 60, 0.5)',
                        color: '#fff',
                        border: '1px solid rgba(255, 0, 0, 0.5)',
                        borderRadius: 1,
                        fontWeight: 'medium',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Typography component="span" sx={{ color: 'rgba(255,255,255,0.7)', mr: 0.5 }}>
                        @
                      </Typography>
                      <Typography component="span">{userData.userHandle}</Typography>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    label="Review Title (Put Name of Movie in Review)"
                    value={threadTitle}
                    onChange={(e) => setThreadTitle(e.target.value)}
                    variant="outlined"
                    margin="normal"
                    required
                    InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                    InputProps={{ sx: { color: '#fff', backgroundColor: 'rgba(60, 60, 60, 0.5)' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.5)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 0, 0, 0.8)' },
                        '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Movie Review"
                    value={threadContent}
                    onChange={(e) => setThreadContent(e.target.value)}
                    variant="outlined"
                    margin="normal"
                    required
                    multiline
                    rows={4}
                    InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                    InputProps={{ sx: { color: '#fff', backgroundColor: 'rgba(60, 60, 60, 0.5)' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.5)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 0, 0, 0.8)' },
                        '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                      }
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                borderTop: '1px solid rgba(255, 0, 0, 0.3)',
                p: 2,
                position: isMobile ? 'sticky' : 'relative',
                bottom: isMobile ? 0 : 'auto',
                backgroundColor: 'rgba(20, 20, 20, 0.95)',
                zIndex: 1000
              }}
            >
              <Button onClick={() => setCreateThreadDialogOpen(false)} sx={{ color: '#fff' }}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateThread}
                variant="contained"
                disabled={!threadTitle || !threadContent}
                sx={{
                  bgcolor: 'rgba(255, 0, 0, 0.7)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 0, 0, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 10px rgba(255, 0, 0, 0.3)'
                  },
                  '&.Mui-disabled': { bgcolor: 'rgba(255, 0, 0, 0.3)', color: 'rgba(255,255,255,0.5)' },
                  transition: 'all 0.2s ease'
                }}
              >
                {movieSearchLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Create Review'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Movie Search Dialog */}
          <Dialog
            open={movieSearchDialogOpen}
            onClose={() => setMovieSearchDialogOpen(false)}
            fullWidth
            maxWidth="lg"
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                border: isMobile ? 'none' : '1px solid rgba(255, 0, 0, 0.3)',
                boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
            TransitionProps={{
              onEnter: () => { if (isMobile) document.body.style.overflow = 'hidden'; },
              onExited: () => { if (isMobile) document.body.style.overflow = 'auto'; }
            }}
          >
            <DialogTitle
              sx={{
                color: '#fff',
                borderBottom: '1px solid rgba(255, 0, 0, 0.3)',
                pb: 2,
                position: isMobile ? 'sticky' : 'relative',
                top: isMobile ? 0 : 'auto',
                zIndex: isMobile ? 1100 : 'auto',
                backgroundColor: 'rgba(0, 0, 0, 0.95)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ImageIcon sx={{ mr: 1, color: '#ff0000' }} /> 
                <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                  Select a Movie Poster
                </Typography>
              </Box>
              <IconButton onClick={() => setMovieSearchDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                pt: 3,
                overflow: 'auto',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255, 0, 0, 0.6)', borderRadius: '4px' },
                '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0, 0, 0, 0.2)' }
              }}
            >
              <Typography variant="subtitle1" color="rgba(255,255,255,0.7)" gutterBottom sx={{ fontStyle: 'italic' }}>
                Search for a horror, thriller, or mystery movie
              </Typography>
              <form onSubmit={handleMovieSearchSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  placeholder="Enter movie title..."
                  value={movieSearchQuery}
                  onChange={handleMovieSearchChange}
                  variant="outlined"
                  InputProps={{
                    sx: { color: '#fff', fontSize: { xs: '1rem', sm: '1.1rem' }, py: 0.5 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" type="submit" size="large" sx={{ color: '#ff0000' }}>
                          <SearchIcon fontSize={isMobile ? 'medium' : 'large'} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 0, 0, 0.5)', borderWidth: 2 },
                      '&:hover fieldset': { borderColor: 'rgba(255, 0, 0, 0.8)' },
                      '&.Mui-focused fieldset': { borderColor: '#ff0000' }
                    }
                  }}
                />
              </form>
              {movieSearchLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexGrow: 1, alignItems: 'center' }}>
                  <CircularProgress sx={{ color: '#ff0000' }} size={60} />
                </Box>
              ) : movieSearchResults.length > 0 ? (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {movieSearchResults.map((movie) => (
                    <Grid item xs={6} sm={4} md={3} key={movie.id}>
                      <Box
                        onClick={() => {
                          setThreadPoster(`${TMDB_IMAGE_BASE_URL}${movie.poster_path}`);
                          setMovieSearchDialogOpen(false);
                          setCreateThreadDialogOpen(true);
                        }}
                        sx={{
                          position: 'relative',
                          paddingTop: '150%',
                          cursor: 'pointer',
                          '&:hover .movie-title': { opacity: 1 }
                        }}
                      >
                        <Box
                          component="img"
                          src={`${TMDB_IMAGE_BASE_URL}${movie.poster_path}`}
                          alt={movie.title}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 1,
                            transition: 'all 0.3s ease',
                            '&:hover': { transform: 'scale(1.05)', filter: 'brightness(1.1)' }
                          }}
                        />
                        <Box
                          className="movie-title"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: '#fff',
                            p: 1,
                            borderRadius: '0 0 4px 4px',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            fontSize: '0.8rem',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {movie.title}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : movieSearchQuery ? (
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mt: 4 }}>
                  No movies found. Try a different search term.
                </Typography>
              ) : null}
            </DialogContent>
            <DialogActions
              sx={{
                borderTop: '1px solid rgba(255, 0, 0, 0.3)',
                p: 2,
                position: isMobile ? 'sticky' : 'relative',
                bottom: isMobile ? 0 : 'auto',
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                zIndex: 1000
              }}
            >
              <Button
                onClick={() => {
                  setMovieSearchDialogOpen(false);
                  setCreateThreadDialogOpen(true);
                }}
                variant="outlined"
                fullWidth={isMobile}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 0, 0, 0.5)',
                  '&:hover': {
                    borderColor: '#ff0000',
                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  },
                  py: isMobile ? 1 : undefined
                }}
              >
                Skip Selection
              </Button>
            </DialogActions>
          </Dialog>

          {/* Horror Tree Link Dialog */}
          <Dialog 
            open={linkDialogOpen} 
            onClose={() => setLinkDialogOpen(false)}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(20, 20, 20, 0.95)',
                border: '1px solid rgba(255, 0, 0, 0.3)',
                boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '100%'
              }
            }}
          >
            <DialogTitle sx={{ 
              borderBottom: '1px solid rgba(255, 0, 0, 0.3)', 
              display: 'flex',
              alignItems: 'center',
              color: '#fff'
            }}>
              <ShareIcon sx={{ mr: 1, color: '#ff0000' }} />
              Public Horror Tree Link
              <IconButton 
                onClick={() => setLinkDialogOpen(false)} 
                sx={{ 
                  position: 'absolute', 
                  right: 8, 
                  top: 8,
                  color: 'rgba(255,255,255,0.7)'
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                Share this link so others can view your horror reviews on Horror Tree:
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(0,0,0,0.3)', 
                borderRadius: 1,
                border: '1px solid rgba(255, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                wordBreak: 'break-all'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#fff',
                    mr: 1,
                    fontFamily: 'monospace'
                  }}
                >
                  {publicLink}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={copyHorrorTreeLink}
                  sx={{
                    minWidth: '100px',
                    backgroundColor: 'rgba(255, 0, 0, 0.7)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 0.9)'
                    },
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    ml: 1
                  }}
                >
                  Copy
                </Button>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 0, 0, 0.3)' }}>
              <Button 
                onClick={() => setLinkDialogOpen(false)}
                sx={{ color: '#fff' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {bioUpdateError && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 0, 0, 0.1)', borderRadius: 1 }}>
              <Typography sx={{ color: '#ff0000' }}>{bioUpdateError}</Typography>
            </Box>
          )}
          {socialUpdateError && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 0, 0, 0.1)', borderRadius: 1 }}>
              <Typography sx={{ color: '#ff0000' }}>{socialUpdateError}</Typography>
            </Box>
          )}

          {/* Snackbar for copy feedback */}
          <Snackbar
            open={copySuccess}
            autoHideDuration={3000}
            onClose={() => setCopySuccess(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setCopySuccess(false)} 
              severity="success" 
              variant="filled"
              sx={{ 
                bgcolor: 'rgba(255, 0, 0, 0.9)', 
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' }
              }}
            >
              Horror Tree link copied to clipboard!
            </Alert>
          </Snackbar>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default UserProfile;